import React, { useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/Button';
import { Card } from 'components/Card';
import { openDecagonChat } from 'components/Decagon';
import { Form } from 'components/HookForm';
import Message from 'components/Message';
import Prompt from 'components/Prompt';
import SaveBar from 'components/SaveBar';
import { updateSmartSpot as updateSmartSpotAction } from 'components/SmartSpot';
import When from 'components/When';
import { PERMISSION, SMARTSPOT_MODE, SMARTSPOT_REASON, VENUE_SERVICE_TYPES } from 'constants/logic';
import { SMARTSPOT_UPDATE_SOURCE } from 'constants/tracking';
import { Title } from 'features/App';
import VenueSmartSpotStatus from 'features/YieldManagement/SmartSpot/VenueSmartSpotStatus/VenueSmartSpotStatusContainer';
import { bem } from 'helpers/class-name';
import Segment from 'helpers/segment';
import {
  selectActiveVenue,
  selectUserHasPermission,
  selectVenueSmartspotData,
  selectVenueSmartspotDataIsLoaded,
} from 'selectors/general';

import {
  HowSmartSpotWorks,
  HowSmartSpotWorksSSOff,
  MinimumSpots,
  ReleaseExtraSpots,
  SmartSpotModel,
  SmartSpotOffCTA,
  SmartSpotOnboardingMinimumSpotsForm,
  SmartSpotOnboardingReleaseExtraSpotsForm,
  SmartSpotFillChart,
} from './Components';

import { trackMinimumSpotsUpdated, trackReleaseExtraSpotsUpdated } from './Components/SmartTools.helpers';
import './SmartToolsV2.scss';

const smartSpotMessages = {
  ...defineMessages({
    contactUs: {
      id: 'manage.smart-spot.contact-us',
      defaultMessage: 'Contact us',
    },
    customize: {
      id: 'manage.smart-spot.customize',
      defaultMessage: 'Customize SmartSpot',
    },
    ineligible: {
      id: 'manage.smart-spot.ineligible',
      defaultMessage:
        '<strong>{venueName}{venueSubtitle, select, undefined {} other { ({venueSubtitle})}}</strong> is not eligible for SmartSpot.  Please select another location.',
    },
    title: {
      id: 'manage.smart-spot.title',
      defaultMessage: 'SmartSpot',
    },
  }),
};

const FORM_NAME = 'smartToolsV2';

const { element, block } = bem('smart-tools-v2');

const fitnessVenue = venue => {
  const { serviceTypes } = venue;
  return serviceTypes && serviceTypes.includes(VENUE_SERVICE_TYPES.CLASS);
};

const SmartToolsV2 = () => {
  const dispatch = useDispatch();

  const [showOnboarding, setShowOnboarding] = useState({
    showOnboardingReleaseExtraSpots: false,
    showOnboardingMinimumSpots: false,
  });

  const { showOnboardingReleaseExtraSpots, showOnboardingMinimumSpots } = showOnboarding;

  const venueData = useSelector(selectActiveVenue);
  const venueSmartspotData = useSelector(selectVenueSmartspotData);
  const venueSmartspotDataIsLoaded = useSelector(selectVenueSmartspotDataIsLoaded);
  const canManageSmartspotVersion = useSelector(state =>
    selectUserHasPermission(state, PERMISSION.CAN_MANAGE_SMARTSPOT_VERSION)
  );

  const { countryCode, venueName, venueSubtitle, isSmartspotEligible, venueId, isManualStudio } = venueData;
  const {
    isEnabled,
    mode = SMARTSPOT_MODE.AGGRESSIVE,
    minimumSpotsPeakTime,
    minimumSpotsOffpeakTime,
    allRemainingSpotsPeakTime,
    allRemainingSpotsOffpeakTime,
  } = venueSmartspotData;

  const handleContactUsClick = () => {
    Segment.event('SmartSpot Contact Us', { venue_id: venueId });
    openDecagonChat();
  };

  const handleLearnMore = location => {
    Segment.event('SmartSpot Learn More', {
      venue_id: venueId,
      location,
    });
  };

  const renderChartContent = () => <SmartSpotFillChart />;

  useEffect(() => {
    setShowOnboarding({ showOnboardingReleaseExtraSpots: false, showOnboardingMinimumSpots: false });
  }, [venueId]);

  useEffect(() => {
    if (venueSmartspotDataIsLoaded && venueId && !showOnboardingReleaseExtraSpots && !showOnboardingMinimumSpots) {
      Segment.event('SmartSpot Landing Page View', {
        smartspot_enabled: isEnabled ? 'on' : 'off',
      });
    }
  }, [venueSmartspotDataIsLoaded, isEnabled, venueId, showOnboardingReleaseExtraSpots, showOnboardingMinimumSpots]);

  useEffect(() => {
    if (venueSmartspotDataIsLoaded && venueId && showOnboardingReleaseExtraSpots) {
      Segment.event('SmartSpot Onboarding - Release Extra Page View', { venue_id: venueId });
    } else if (venueSmartspotDataIsLoaded && venueId && showOnboardingMinimumSpots) {
      Segment.event('SmartSpot Onboarding - Minimum Spots Page View', { venue_id: venueId });
    }
  }, [venueId, venueSmartspotDataIsLoaded, showOnboardingReleaseExtraSpots, showOnboardingMinimumSpots]);

  const initialValues = {
    isEnabled,
    mode,
    minimumSpotsPeakTime,
    minimumSpotsOffpeakTime,
    releaseExtraSpotsPeakTime: allRemainingSpotsPeakTime,
    releaseExtraSpotsOffPeakTime: allRemainingSpotsOffpeakTime,
  };

  const showSelectOptionMinimumSpotsPeakTime = minimumSpotsPeakTime === null || minimumSpotsPeakTime === 0;
  const showSelectOptionMinimumSpotsOffpeakTime = minimumSpotsOffpeakTime === null || minimumSpotsOffpeakTime === 0;
  const showSelectOptionReleaseExtraSpotsPeakTime =
    allRemainingSpotsPeakTime === null || allRemainingSpotsPeakTime === 0;
  const showSelectOptionReleaseExtraSpotsOffPeakTime =
    allRemainingSpotsOffpeakTime === null || allRemainingSpotsOffpeakTime === 0;

  const handleSubmit = (formValues, formAsyncFns) => {
    const {
      mode: formMode,
      minimumSpotsPeakTime: formMinimumSpotsPeakTime,
      minimumSpotsOffpeakTime: formMinimumSpotsOffpeakTime,
      releaseExtraSpotsPeakTime,
      releaseExtraSpotsOffPeakTime,
      ...restFormValues
    } = formValues;

    const values = {
      ...restFormValues,
      mode: formMode,
      minimumSpotsPeakTime: formMinimumSpotsPeakTime,
      minimumSpotsOffpeakTime: formMinimumSpotsOffpeakTime,
      allRemainingSpotsPeakTime: releaseExtraSpotsPeakTime,
      allRemainingSpotsOffpeakTime: releaseExtraSpotsOffPeakTime,
    };

    const modeChanged = formMode !== initialValues.mode;
    if (modeChanged) {
      const properties = {
        venue_id: venueId,
        model: formMode,
      };
      Segment.event('SmartSpot Model Updated', properties);
    }

    trackMinimumSpotsUpdated(venueId, formMinimumSpotsPeakTime, formMinimumSpotsOffpeakTime, initialValues);
    trackReleaseExtraSpotsUpdated(venueId, releaseExtraSpotsPeakTime, releaseExtraSpotsOffPeakTime, initialValues);

    const meta = {
      venueId,
      venueName,
      venueSubtitle,
      reason: SMARTSPOT_REASON.GENERIC_UPDATE,
      source: SMARTSPOT_UPDATE_SOURCE.SMART_TOOLS,
    };

    return dispatch(updateSmartSpotAction(values, meta, formAsyncFns));
  };

  const isSmartSpotIneligible = !isSmartspotEligible && !(isManualStudio && fitnessVenue(venueData));

  return (
    <div className={block()}>
      <Message message={smartSpotMessages.title} as={Title} />
      <When predicate={!venueSmartspotDataIsLoaded}>
        <Card loading />
      </When>
      <When predicate={showOnboardingReleaseExtraSpots}>
        <SmartSpotOnboardingReleaseExtraSpotsForm
          formName={FORM_NAME}
          isLoading={!venueSmartspotDataIsLoaded}
          onSubmit={handleSubmit}
          countryCode={countryCode}
          handleLearnMore={handleLearnMore}
          onSkip={() => {
            setShowOnboarding({ showOnboardingReleaseExtraSpots: false, showOnboardingMinimumSpots: true });
            Segment.event('SmartSpot Onboarding - Release extra skipped', { venue_id: venueId });
          }}
          onNext={() => {
            setShowOnboarding({ showOnboardingReleaseExtraSpots: false, showOnboardingMinimumSpots: true });
          }}
          showSelectOptionPeakTime={showSelectOptionReleaseExtraSpotsPeakTime}
          showSelectOptionOffPeakTime={showSelectOptionReleaseExtraSpotsOffPeakTime}
        />
      </When>
      <When predicate={showOnboardingMinimumSpots}>
        <SmartSpotOnboardingMinimumSpotsForm
          formName={FORM_NAME}
          isLoading={!venueSmartspotDataIsLoaded}
          onSubmit={handleSubmit}
          countryCode={countryCode}
          handleLearnMore={handleLearnMore}
          onSkip={() => {
            setShowOnboarding({ showOnboardingReleaseExtraSpots: false, showOnboardingMinimumSpots: false });
            Segment.event('SmartSpot Onboarding - Minimum spots skipped', { venue_id: venueId });
          }}
          onDone={() => {
            setShowOnboarding({ showOnboardingReleaseExtraSpots: false, showOnboardingMinimumSpots: false });
          }}
          showSelectOptionPeakTime={showSelectOptionMinimumSpotsPeakTime}
          showSelectOptionOffPeakTime={showSelectOptionMinimumSpotsOffpeakTime}
        />
      </When>
      <When
        predicate={
          venueSmartspotDataIsLoaded &&
          isSmartSpotIneligible &&
          !showOnboardingReleaseExtraSpots &&
          !showOnboardingMinimumSpots
        }>
        <Card centered>
          <Message
            message={smartSpotMessages.ineligible}
            values={{
              venueName,
              venueSubtitle: venueSubtitle || undefined,
              strong: text => <strong>{text}</strong>,
            }}
          />
        </Card>
      </When>
      <When
        predicate={
          venueSmartspotDataIsLoaded &&
          !isSmartSpotIneligible &&
          !showOnboardingReleaseExtraSpots &&
          !showOnboardingMinimumSpots
        }>
        <When predicate={!isEnabled}>
          <SmartSpotOffCTA
            fitnessVenue={fitnessVenue}
            venueData={venueData}
            venueSmartspotDataIsLoaded={venueSmartspotDataIsLoaded}
            isSmartspotEligible={isSmartspotEligible}
            setShowOnboarding={setShowOnboarding}
          />
          <HowSmartSpotWorksSSOff />
        </When>
        <When predicate={isEnabled && !showOnboardingReleaseExtraSpots}>
          <Form
            id={FORM_NAME}
            defaultValues={initialValues}
            isLoading={!venueSmartspotDataIsLoaded}
            onSubmit={handleSubmit}>
            {function FormRender({ control, formState, reset }) {
              useEffect(() => {
                reset(initialValues);
              }, [initialValues]);

              const { isDirty, isSubmitting } = formState;

              return (
                <>
                  <Prompt when={isDirty} />
                  <div className={element('heading-text')}>
                    <VenueSmartSpotStatus />
                    <Button className={element('contact-link')} styled="inline-link" onClick={handleContactUsClick}>
                      <Message message={smartSpotMessages.contactUs} />
                    </Button>
                  </div>
                  <div className={element('info-container')}>
                    <div className={element('info-chart-item')}>{renderChartContent()}</div>
                    <div className={element('info-how-item')}>
                      <HowSmartSpotWorks handleLearnMore={handleLearnMore} />
                    </div>
                  </div>
                  <div className={element('heading-line')}>
                    <Message message={smartSpotMessages.customize} />
                  </div>
                  <ReleaseExtraSpots
                    control={control}
                    countryCode={countryCode}
                    handleLearnMore={handleLearnMore}
                    showSelectOptionPeakTime={showSelectOptionReleaseExtraSpotsPeakTime}
                    showSelectOptionOffPeakTime={showSelectOptionReleaseExtraSpotsOffPeakTime}
                  />
                  <MinimumSpots
                    control={control}
                    countryCode={countryCode}
                    handleLearnMore={handleLearnMore}
                    showSelectOptionPeakTime={showSelectOptionMinimumSpotsPeakTime}
                    showSelectOptionOffPeakTime={showSelectOptionMinimumSpotsOffpeakTime}
                  />
                  <When predicate={canManageSmartspotVersion}>
                    <SmartSpotModel />
                  </When>
                  <SaveBar visible={isDirty} submitting={isSubmitting} formName={FORM_NAME} />
                </>
              );
            }}
          </Form>
        </When>
      </When>
    </div>
  );
};

export default SmartToolsV2;
